import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
import { usePlatformSource } from './usePlatformSource';
import { useQueryParam } from './useQueryParam';
import { usePageType } from './usePageType';
import { PageType } from './usePageType/usePageType.types';
import { QUERY } from '@/constants/common';
import { GetAdDetailsResponse } from '@/api/types/ads.types';
import { GET_AD_DETAILS } from '@/api/queries/ads.queries';
import { getPathWithoutHash, hideParamFromUrl } from '@/utils/helpers';

const usePreviousRoute = () => {
  const { asPath } = useRouter();
  const ref = useRef<string | null>(null);
  const previousRef = useRef<string | null>(null);

  useEffect(() => {
    previousRef.current = ref.current;
    ref.current = asPath;
  }, [asPath]);

  return previousRef.current;
};

export function useAdDetails({ skipQuery = false } = {}) {
  const {
    query: { slug },
    asPath,
  } = useRouter();
  const source = usePlatformSource();
  const [transitionSourceBlock] = useQueryParam(QUERY.transitionSourceBlock);
  const pageType = usePageType();
  const previousAsPath = usePreviousRoute();
  const cleanPath = getPathWithoutHash(asPath);
  const cleanPreviousPath = getPathWithoutHash(previousAsPath);

  const legacyId = slug?.[2];
  const isAdPage = pageType === PageType.Ad && !!legacyId;

  const { data, loading, refetch, previousData } =
    useQuery<GetAdDetailsResponse>(GET_AD_DETAILS, {
      variables: {
        legacyId,
        source,
        ...(transitionSourceBlock && { transitionSourceBlock }),
      },
      errorPolicy: 'all',
      fetchPolicy: 'cache-first',
      nextFetchPolicy: 'network-only',
      skip: !isAdPage || skipQuery,
    });

  useEffect(() => {
    if (previousAsPath === null) {
      return;
    }

    if (cleanPreviousPath !== cleanPath && legacyId) {
      refetch();
    }
  }, [cleanPath, cleanPreviousPath, legacyId, refetch]);

  useEffect(() => {
    if (isAdPage) {
      hideParamFromUrl(QUERY.transitionSourceBlock);
    }
  }, [isAdPage]);

  return { adDetails: data?.adDetails, loading, refetch, previousData };
}
